import { useEffect, useRef } from "react";
import useSWR, { useSWRConfig } from "swr";
import { apFetcher } from "../Utilities/axiosPrivateFetcher_SWR";
import { AUTH_SWR_KEYS, clearAuthenticatedCache } from "../Utilities/swrKeys";
import { useAuth } from "../../AuthContext";
/**
 * Hydrates authenticated user data.
 * This component should be placed inside the AuthContextProvider so it can access authentication state.
 * Data will only be fetched when the user is authenticated (null key pattern).
 *
 * Data hydrated here:
 * - Customer info
 * - Delivery addresses
 * - Payment methods
 * - My Meals menu data
 * - Skip days data
 *
 * See HYDRATION_README.md for details.
 * @returns null
 */
export const AuthenticatedDataHydrator = () => {
    const { isAuthenticated } = useAuth();
    const { mutate } = useSWRConfig();
    // The session can end without a page reload (expired token, failed refresh, logout). Drop the previous
    // user's data from memory so the next login in this tab cannot read it.
    const wasAuthenticated = useRef(false);
    useEffect(() => {
        if (wasAuthenticated.current && !isAuthenticated) {
            clearAuthenticatedCache(mutate);
        }
        wasAuthenticated.current = isAuthenticated;
    }, [isAuthenticated, mutate]);
    // Hydrate delivery addresses data on load to ensure it's available globally.
    // These addresses are (currently) program, plan, and delivery schedule agnostic.
    // ! The customer can edit these addresses, so we need to update the cache when they do.
    useSWR(isAuthenticated ? AUTH_SWR_KEYS.DELIVERY_ADDRESSES : null, apFetcher(`/Account/GetAccountDeliveryAddresses`), {
        revalidateOnMount: true,
    });
    // Hydrate customer info data on load to ensure it's available globally.
    // ! The customer can edit this info, so we need to update the cache when they do.
    useSWR(isAuthenticated ? AUTH_SWR_KEYS.CUSTOMER_INFO : null, apFetcher(`/Account/CustomerInfo`), {
        revalidateOnMount: true,
    });
    // Hydrate customer payment methods data on load to ensure it's available globally.
    // ! The customer can edit these payment methods, so we need to update the cache when they do.
    useSWR(isAuthenticated ? AUTH_SWR_KEYS.PAYMENT_METHODS : null, apFetcher(`/GetPaymentMethods?type=Card&type=GiftCard`), {
        revalidateOnMount: true,
    });
    // Hydrate My Meals menu data on load to ensure instant menu rendering.
    // Uses optimized slim endpoint without expensive cutoff calculations.
    // ! This data can change when customer orders are modified, or when admin changes menus.
    useSWR(isAuthenticated ? AUTH_SWR_KEYS.MY_MEALS_MENU : null, apFetcher(`/MyMeals/GetMyMealsMenuData`), {
        revalidateOnMount: true,
    });
    // Hydrate skip days data on load to ensure instant availability across Orders and MyMeals pages.
    // ! The customer can modify skip days, so mutations use populateCache to keep cache synchronized.
    useSWR(isAuthenticated ? AUTH_SWR_KEYS.SKIP_DAYS : null, apFetcher(`/Account/GetSkipDays`), {
        revalidateOnMount: true,
    });
    return null;
};
